'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedCallback } from '@kit/ui/hooks/use-debounce';

import { useGoogleMapsAPI } from '../../../lib/use-google-maps';
import type { AddressValue } from './types';

// =====================================================================
// Google Maps type declarations (minimal, internal)
// =====================================================================

interface AutocompleteRequest {
  input: string;
  componentRestrictions?: Record<string, unknown>;
  [key: string]: unknown;
}

interface GooglePlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text?: string;
  };
  [key: string]: unknown;
}

interface PlaceDetailsRequest {
  placeId: string;
  fields?: string[];
}

interface GooglePlaceDetails {
  address_components?: AddressComponent[];
  formatted_address?: string;
  [key: string]: unknown;
}

export interface AutocompletePrediction {
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

// =====================================================================
// Constants
// =====================================================================

/**
 * Common-market country allowlist for the Places `componentRestrictions`.
 * Hardcoding a single default ('ch') stranded every non-Swiss dancer
 * (Sentry thread + Jieun Aurora Vienna report 2026-05-21): the
 * predictions API silently returns ZERO_RESULTS for foreign addresses
 * and the user has no cue. List covers Ballee's active dancer
 * geographies.
 */
export const ADDRESS_AUTOCOMPLETE_COUNTRIES = [
  'ch',
  'at',
  'de',
  'fr',
  'it',
  'gb',
  'us',
  'es',
  'nl',
  'be',
  'pt',
  'pl',
  'ie',
  'lu',
  'li',
] as const;

/** Hard cap on how long we wait for a Google Places callback to fire. */
export const PLACES_CALLBACK_TIMEOUT_MS = 5_000;

/**
 * Trigger threshold for the "Autocomplete unavailable" hint. We tolerate
 * one transient timeout (e.g. user typed faster than the network) before
 * we declare autocomplete dead and offer the manual-entry hint.
 */
export const PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS = 2;

// =====================================================================
// Helpers
// =====================================================================

function withTimeout<T>(
  executor: (
    resolve: (value: T) => void,
    reject: (reason: Error) => void,
  ) => void,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);

    executor(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

// =====================================================================
// Hook
// =====================================================================

export type ResolvedPlace = Omit<AddressValue, 'apartment'> & {
  placeId: string;
};

export interface UsePlacesParams {
  apiKey: string | undefined;
  /** ISO-3166-1 alpha-2 country picked in the form, if any (used to narrow predictions). */
  pickedCountryCode: string | undefined;
  /**
   * Notified when the Places integration fails. The wrapper component
   * forwards this to the consumer's `onAutocompleteError` prop and to a
   * `console.warn` breadcrumb for Sentry.
   */
  onError: (error: Error, op: 'getDetails' | 'getPredictions' | 'load') => void;
}

export interface UsePlacesResult {
  /** Predictions to render in the dropdown. */
  placePredictions: AutocompletePrediction[];
  /** True while predictions are in-flight. Drives the input-trailing spinner. */
  isPredictionsLoading: boolean;
  /** True while a `getDetails` call is resolving. Also drives the spinner. */
  isLoadingPlace: boolean;
  /**
   * True when the autocomplete is declared unavailable (no API key, load
   * error, or 2+ consecutive callback timeouts). The component renders
   * the manual-entry hint and stops re-arming the spinner.
   */
  autocompleteFailed: boolean;
  /**
   * Debounced predictions trigger. Pass the current Street Address text
   * — emits at most one Places request every 350ms.
   */
  getPlacePredictions: (input: string) => void;
  /**
   * Resolves a selected `place_id` into the structured address slot.
   * Returns `null` on failure (already reported via `onError`).
   */
  resolvePlaceDetails: (placeId: string) => Promise<ResolvedPlace | null>;
  /** Clear predictions (e.g. after a selection or on close). */
  clearPredictions: () => void;
}

/**
 * Encapsulates the Google Places lifecycle: API load, AutocompleteService
 * + PlacesService instantiation, debounced predictions with timeout +
 * fail-open hint state, and `getDetails` parsing. Keep the component
 * itself focused on rendering + RHF wiring.
 */
export function usePlaces({
  apiKey,
  pickedCountryCode,
  onError,
}: UsePlacesParams): UsePlacesResult {
  // Stable ref so debounced closures don't capture a stale handler.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const reportFailure = useCallback(
    (error: Error, op: 'getDetails' | 'getPredictions' | 'load') => {
      // Greppable client breadcrumb. Sentry instrumentation captures
      // console.warn as a breadcrumb on the wrapping page.
      console.warn(`[address-autocomplete] ${op} failed: ${error.message}`);
      onErrorRef.current?.(error, op);
    },
    [],
  );

  // ---------------------------------------------------------------------
  // API loader
  // ---------------------------------------------------------------------
  const { error: googleError, isLoaded: isGoogleLoaded } = useGoogleMapsAPI({
    apiKey: apiKey ?? '',
    libraries: ['places'],
  });

  const [autocompleteFailed, setAutocompleteFailed] = useState(false);
  const consecutiveTimeoutsRef = useRef(0);

  // Fail-open when Google Maps itself failed to load OR when the API key
  // is missing. Manual entry stays available regardless.
  useEffect(() => {
    if (!apiKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- script-loader effect (external-system sync); the failure flag is also set from async load/error callbacks, so it can't be derived purely during render.
      setAutocompleteFailed(true);
      return;
    }
    if (googleError) {
      setAutocompleteFailed(true);
      reportFailure(googleError, 'load');
    }
  }, [apiKey, googleError, reportFailure]);

  // ---------------------------------------------------------------------
  // Services
  // ---------------------------------------------------------------------
  const autocompleteService = useRef<{
    getPlacePredictions(
      request: AutocompleteRequest,
      callback: (
        predictions: GooglePlacePrediction[] | null,
        status: string,
      ) => void,
    ): void;
  } | null>(null);

  useEffect(() => {
    if (isGoogleLoaded && apiKey && window.google?.maps?.places) {
      autocompleteService.current = new (
        window.google.maps.places as unknown as {
          AutocompleteService: new () => typeof autocompleteService.current;
        }
      ).AutocompleteService() as typeof autocompleteService.current;
    }
  }, [isGoogleLoaded, apiKey]);

  // Resolve component-restrictions country list. If the form has a
  // 2-letter country picked, restrict to that for relevance; otherwise
  // fall back to the active-geography allowlist so non-Swiss dancers
  // aren't stranded on a zero-results loop.
  const countryRestriction = useMemo<string | readonly string[]>(() => {
    const picked = pickedCountryCode?.toLowerCase();
    if (picked && picked.length === 2) return picked;
    return ADDRESS_AUTOCOMPLETE_COUNTRIES;
  }, [pickedCountryCode]);

  // ---------------------------------------------------------------------
  // Predictions
  // ---------------------------------------------------------------------
  const [placePredictions, setPlacePredictions] = useState<
    AutocompletePrediction[]
  >([]);
  const [isPredictionsLoading, setIsPredictionsLoading] = useState(false);

  const requestPlacePredictions = useCallback(
    (input: string) => {
      if (!autocompleteService.current || !input.trim()) {
        setPlacePredictions([]);
        setIsPredictionsLoading(false);
        return;
      }

      setIsPredictionsLoading(true);

      const request: AutocompleteRequest = {
        input,
        types: ['address'],
        componentRestrictions: { country: countryRestriction },
      };

      withTimeout<AutocompletePrediction[]>(
        (resolve, reject) => {
          autocompleteService.current!.getPlacePredictions(
            request,
            (predictions: GooglePlacePrediction[] | null, status: string) => {
              const places = window.google?.maps?.places as unknown as {
                PlacesServiceStatus?: {
                  OK?: string;
                  ZERO_RESULTS?: string;
                };
              };
              const okStatus = places?.PlacesServiceStatus?.OK;
              const zeroResultsStatus =
                places?.PlacesServiceStatus?.ZERO_RESULTS;

              if (status === okStatus && predictions) {
                resolve(predictions as unknown as AutocompletePrediction[]);
                return;
              }
              if (status === zeroResultsStatus) {
                resolve([]);
                return;
              }
              reject(new Error(`Places predictions status=${status}`));
            },
          );
        },
        PLACES_CALLBACK_TIMEOUT_MS,
        'getPlacePredictions',
      )
        .then((predictions) => {
          consecutiveTimeoutsRef.current = 0;
          setPlacePredictions(predictions);
        })
        .catch((error: Error) => {
          setPlacePredictions([]);
          const isTimeout = error.message.includes('timed out');
          if (isTimeout) {
            consecutiveTimeoutsRef.current += 1;
            if (
              consecutiveTimeoutsRef.current >=
              PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS
            ) {
              setAutocompleteFailed(true);
            }
          }
          reportFailure(error, 'getPredictions');
        })
        .finally(() => {
          setIsPredictionsLoading(false);
        });
    },
    [countryRestriction, reportFailure],
  );

  // Debounce predictions by 350ms — the Places service is rate-limited
  // and un-debounced rapid typing produces overlapping in-flight
  // requests that can silently drop their callbacks (the original
  // Jieun-stuck-spinner failure mode).
  const getPlacePredictions = useDebouncedCallback(
    requestPlacePredictions as (...args: unknown[]) => void,
    350,
  ) as (input: string) => void;

  const clearPredictions = useCallback(() => {
    setPlacePredictions([]);
  }, []);

  // ---------------------------------------------------------------------
  // Details
  // ---------------------------------------------------------------------
  const [isLoadingPlace, setIsLoadingPlace] = useState(false);

  const resolvePlaceDetails = useCallback(
    async (placeId: string): Promise<ResolvedPlace | null> => {
      if (!window.google || !apiKey) return null;
      const places = window.google?.maps?.places;
      if (!places) {
        reportFailure(new Error('Places library not available'), 'getDetails');
        return null;
      }

      setIsLoadingPlace(true);
      try {
        const place = await withTimeout<GooglePlaceDetails>(
          (resolve, reject) => {
            const service = new (
              places as unknown as {
                PlacesService: new (el: HTMLElement) => {
                  getDetails(
                    request: PlaceDetailsRequest,
                    callback: (
                      result: GooglePlaceDetails | null,
                      status: string,
                    ) => void,
                  ): void;
                };
              }
            ).PlacesService(document.createElement('div'));
            service.getDetails(
              {
                placeId,
                fields: ['address_components', 'formatted_address', 'place_id'],
              },
              (result, status) => {
                const okStatus = (
                  places as unknown as {
                    PlacesServiceStatus?: { OK?: string };
                  }
                ).PlacesServiceStatus?.OK;
                if (status === okStatus && result) {
                  resolve(result);
                } else {
                  reject(new Error(`Places details status=${status}`));
                }
              },
            );
          },
          PLACES_CALLBACK_TIMEOUT_MS,
          'getDetails',
        );

        const components = place.address_components ?? [];

        let streetNumber = '';
        let streetName = '';
        let city = '';
        let stateProvince = '';
        let postalCode = '';
        let countryCode = '';

        components.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            streetName = component.long_name;
          }
          if (
            types.includes('locality') ||
            types.includes('administrative_area_level_2')
          ) {
            city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            stateProvince = component.short_name;
          }
          if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
          if (types.includes('country')) {
            countryCode = component.short_name;
          }
        });

        consecutiveTimeoutsRef.current = 0;

        return {
          streetAddress: `${streetNumber} ${streetName}`.trim(),
          city,
          stateProvince,
          postalCode,
          countryCode,
          placeId: (place as { place_id?: string }).place_id ?? placeId,
        };
      } catch (rawError) {
        const error =
          rawError instanceof Error ? rawError : new Error(String(rawError));
        if (error.message.includes('timed out')) {
          consecutiveTimeoutsRef.current += 1;
          if (
            consecutiveTimeoutsRef.current >=
            PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS
          ) {
            setAutocompleteFailed(true);
          }
        }
        reportFailure(error, 'getDetails');
        return null;
      } finally {
        setIsLoadingPlace(false);
      }
    },
    [apiKey, reportFailure],
  );

  return {
    placePredictions,
    isPredictionsLoading,
    isLoadingPlace,
    autocompleteFailed,
    getPlacePredictions,
    resolvePlaceDetails,
    clearPredictions,
  };
}
